import {
  AbstractMesh,
  Mesh,
  MirrorTexture,
  PBRMaterial,
  Plane,
  Scene,
} from "@babylonjs/core";
import { isTouchDevice } from "../util/device.js";

/**
 * Planar reflection for the atrium floor: the signature "polished mall floor
 * mirroring the storefronts and skylight" look. Desktop-only; the mirror
 * re-renders its list every frame (planar reflections are view-dependent, so
 * the static-scene render-once trick can't apply), which is exactly the cost
 * phones shed. Phones keep the IBL gloss from the environment texture.
 *
 * Applied ONLY to the mm-atrium-floor material: it is the hero surface, and
 * unlike the corridor/shop floor materials it is used by exactly one mesh at
 * exactly y=0, which is what a single mirror plane requires.
 */

/** True for meshes worth seeing in the floor: visible, lit geometry that is
 *  not the floor itself, not a fake-shadow decal, and not (mostly) below the
 *  reflection plane. Exported for tests. */
export function shouldReflect(mesh: AbstractMesh, floor: AbstractMesh): boolean {
  if (mesh === floor || !mesh.isVisible || mesh.material === null || mesh.getTotalVertices() === 0) {
    return false;
  }
  if (mesh.material.name === "mm-contact-shadow") {
    return false;
  }
  return mesh.getBoundingInfo().boundingBox.maximumWorld.y > 0.05;
}

export function addAtriumFloorReflection(scene: Scene, options?: { touch?: boolean }): void {
  const touch: boolean = options?.touch === undefined ? isTouchDevice() : options.touch;
  if (touch) {
    return;
  }
  const floor: AbstractMesh | null = scene.getMeshByName("atrium-floor");
  const material: PBRMaterial | null = scene.getMaterialByName("mm-atrium-floor") as PBRMaterial | null;
  if (floor === null || material === null) {
    return;
  }
  const mirror: MirrorTexture = new MirrorTexture("mm-floor-mirror", { ratio: 0.5 }, scene, true);
  mirror.mirrorPlane = new Plane(0, -1, 0, 0); // reflect across y=0
  // Heavy blur + reduced level, screenshot-tuned: at kernel 24 / level 1 the
  // jumbotron reflected as hard black blotches (wet marble, not porcelain).
  mirror.adaptiveBlurKernel = 48;
  mirror.level = 0.75;
  const list: Mesh[] = [];
  for (const mesh of scene.meshes) {
    if (mesh instanceof Mesh && shouldReflect(mesh, floor)) {
      list.push(mesh);
    }
  }
  mirror.renderList = list;
  // Replaces the IBL env for this one material. With metallic 0 the fresnel
  // term keeps the reflection subtle head-on and strong at grazing angles,
  // which is exactly how a polished floor reads in a real mall.
  material.reflectionTexture = mirror;
}
